import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, User, Bike, HandCoins, Loader2, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { formatMoney } from '../lib/money';
import api from '../services/api';
import { toast } from 'sonner';

export default function MarketplaceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { delivery, startMarketplaceCheckout } = useApp();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(null);
  const [mainIdx, setMainIdx] = useState(0);
  const [mode, setMode] = useState('full'); // full | delivery_only
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/listings/${id}`)
      .then((r) => setL(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Listing not found'));
  }, [id]);

  const distanceKm = (() => {
    if (!l || delivery?.lat == null || l.lat == null) return null;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(l.lat - delivery.lat);
    const dLng = toRad(l.lng - delivery.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(delivery.lat)) * Math.cos(toRad(l.lat)) * Math.sin(dLng / 2) ** 2;
    return (2 * 6371 * Math.asin(Math.sqrt(a))).toFixed(1);
  })();

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${id}/prepare-checkout`);
      startMarketplaceCheckout(data.vendor, data.item, mode);
      toast.success(mode === 'delivery_only'
        ? 'Added — you pay the seller directly; ShipitNow charges delivery only.'
        : 'Added — pay everything through ShipitNow at checkout.');
      nav('/checkout');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout'); }
    finally { setBuying(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50"><Header showSearch={false} />
      <main className="max-w-2xl mx-auto px-4 py-16 text-center text-slate-500">{err}</main></div>
  );
  if (!l) return (
    <div className="min-h-screen bg-slate-50"><Header showSearch={false} />
      <main className="max-w-2xl mx-auto px-4 py-16 text-center text-slate-400">Loading…</main></div>
  );

  const media = [...(l.photos || []).map((p) => ({ type: 'img', src: p })), ...(l.video ? [{ type: 'video', src: l.video }] : [])];
  const main = media[mainIdx] || media[0];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav('/marketplace')} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Marketplace
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="aspect-square bg-white border border-slate-200 rounded-2xl overflow-hidden flex items-center justify-center" data-testid="listing-main-media">
              {main?.type === 'video'
                ? <video src={main.src} controls className="w-full h-full object-contain bg-black" data-testid="listing-video-player" />
                : main && <img src={main.src} alt={l.title} className="w-full h-full object-cover" />}
            </div>
            {media.length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto">
                {media.map((m, i) => (
                  <button key={i} onClick={() => setMainIdx(i)}
                    className={`h-16 w-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === mainIdx ? 'border-emerald-500' : 'border-slate-200'}`}
                    data-testid={`listing-thumb-${i}`}>
                    {m.type === 'video'
                      ? <div className="h-full w-full bg-slate-900 text-white flex items-center justify-center text-[10px]">Video</div>
                      : <img src={m.src} alt="" className="h-full w-full object-cover" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="listing-title-heading">{l.title}</h1>
            <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="listing-price-display">{formatMoney(l.price, l.currency)}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {l.seller_name}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {l.city}{distanceKm ? ` · ${distanceKm} km away` : ''}</span>
              <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Admin-approved</span>
            </div>
            <p className="text-slate-600 mt-4 whitespace-pre-wrap text-sm">{l.description}</p>

            <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">How do you want to pay?</div>
              <div className="mt-2 space-y-2">
                <button onClick={() => setMode('full')}
                  className={`w-full text-left rounded-xl border-2 p-3 transition ${mode === 'full' ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="buy-mode-full">
                  <div className="flex items-center gap-2 font-medium text-slate-900 text-sm"><Bike className="h-4 w-4 text-emerald-600" /> Pay through ShipitNow</div>
                  <p className="text-xs text-slate-500 mt-0.5">Item + delivery + service in one checkout. A ShipitNow driver picks up from the seller and delivers to you.</p>
                </button>
                <button onClick={() => setMode('delivery_only')}
                  className={`w-full text-left rounded-xl border-2 p-3 transition ${mode === 'delivery_only' ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="buy-mode-delivery-only">
                  <div className="flex items-center gap-2 font-medium text-slate-900 text-sm"><HandCoins className="h-4 w-4 text-amber-600" /> Pay the seller directly</div>
                  <p className="text-xs text-slate-500 mt-0.5">You settle the item price with the seller yourselves — ShipitNow only charges delivery + service and the driver delivers it.</p>
                </button>
              </div>
              <Button onClick={buy} disabled={buying} className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="buy-now-btn">
                {buying && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Buy — go to checkout
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
